import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/ui/table/data-table";
import { useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { displayValue } from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import { MembershipFacility } from "@/types/member";
import { Separator } from "@/components/ui/separator";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { memo } from "react";
import {
  useGetMembershipFacilityQuery,
  useUpdateMembershipFacilityCreditsMutation,
} from "@/services/memberApi";
import { ErrorType } from "@/types";
import { toast } from "@/components/ui/use-toast";
import { CreditType } from "@/types/enums";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
const initialValue = {
  limit: 5,
  offset: 0,
  sort_order: "desc",
  sort_key: "facility_id",
};

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
}
const MemberMembershipFacilities = () => {
  const { memberId } = useParams();
  const [searchCriteria, setSearchCriteria] =
    useState<SearchCriteriaType>(initialValue);
  const [query, setQuery] = useState("");

  // State to track the editing row and its updated credit value
  const [editingRowId, setEditingRowId] = useState<{
    facilityId: number;
    sourceId: number;
  } | null>(null);

  const [editedCredit, setEditedCredit] = useState<number>(0);

  const [
    UpdateCredits,
    { isLoading: updateCreditsLoading, isError: updateCreditsError },
  ] = useUpdateMembershipFacilityCreditsMutation();

  useEffect(() => {
    setSearchCriteria((prev) => {
      const newCriteria = { ...prev };
      newCriteria.offset = 0;
      newCriteria.sort_key = "facility_id";
      newCriteria.sort_order = "desc";
      return newCriteria;
    });
  }, [setSearchCriteria]);

  useEffect(() => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(searchCriteria)) {
      console.log({ key, value });
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    }
    const newQuery = params.toString();
    console.log({ newQuery });
    setQuery(newQuery);
  }, [searchCriteria]);

  const {
    data: membershipData,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetMembershipFacilityQuery(
    { member_id: Number(memberId), query: query },
    {
      skip: query == "",
    }
  );
  const tableData = useMemo(() => {
    return Array.isArray(membershipData?.data) ? membershipData?.data : [];
  }, [membershipData]);

  const toggleSortOrder = (key: string, order: string) => {
    setSearchCriteria((prev) => ({
      ...prev,
      sort_key: key,
      sort_order: order,
    }));
  };

  const handleEdit = (
    facilityId: number,
    sourceId: number,
    currentCredit: number
  ) => {
    setEditingRowId({ facilityId, sourceId }); // Track both IDs
    setEditedCredit(currentCredit); // Set initial credit value
  };

  const handleSave = async (
    facilityId: number,
    membershipplan_id: number,
    sourceId: number,
    sourceType: number
  ) => {
    if (
      editedCredit === null ||
      editingRowId === null ||
      editingRowId.facilityId !== facilityId ||
      editingRowId.sourceId !== sourceId
    )
      return;

    const payload = {
      member_id: Number(memberId),
      facility_id: facilityId,
      membership_plan_id: sourceId,
      credits: editedCredit,
      source_id: sourceId,
      source_type_id: sourceType,
    };

    try {
      await handleCreditChanges(payload); // Update credits via API
      setEditingRowId(null); // Reset edit mode
      setEditedCredit(0); // Clear edited credit value
    } catch (error) {
      console.error("Failed to save changes", error);
    }
  };

  const handleCreditChanges = async (payload: {
    source_id: number;
    source_type_id: number;
    member_id: number;
    facility_id: number;
    credits: number;
  }) => {
    try {
      const resp = await UpdateCredits(payload).unwrap();
      if (resp) {
        refetch();
        toast({
          variant: "success",
          title: "Credits Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in updation",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Updation",
          description: `Something Went Wrong.`,
        });
      }
    }
  };
  const columns: ColumnDef<MembershipFacility>[] = [
    {
      accessorKey: "membershipname",
      meta: "membershipname",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Membership Plan"
          sortKey="source_name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.source_name)}
          </div>
        );
      },
    },
    {
      accessorKey: "Name",
      meta: "Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="facility_name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.facility_name)}
          </div>
        );
      },
    },
    {
      accessorKey: "credit",
      meta: "credit",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Available Credits"
          sortKey="credits"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const isEditing =
          editingRowId?.facilityId === row.original.facility_id &&
          editingRowId?.sourceId === row.original.source_id;
        const creditType = row.original.credit_type;

        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {isEditing ? (
              <EditableCreditInput
                value={editedCredit}
                onChange={(value: number) => setEditedCredit(value)}
                disable={false}
              />
            ) : creditType === CreditType.unlimited ? (
              <>Unlimited</>
            ) : (
              displayValue(row.original.credits)
            )}
            {row.original.credits > 0 ? " Credits" : " Credits"}
          </div>
        );
      },
    },
    {
      accessorKey: "credit_type",
      meta: "credit_type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Credit Type"
          sortKey="credit_type"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.credit_type)}
          </div>
        );
      },
    },

    {
      accessorKey: "action",
      meta: "actions",
      header: () => "Action",
      cell: ({ row }) => {
        const isEditing =
          editingRowId?.facilityId === row.original.facility_id &&
          editingRowId?.sourceId === row.original.source_id;
        const creditType = row.original.credit_type;

        // Allow action only for rows with credit_type === CreditType.amount
        const canEdit = creditType === CreditType.amount;

        return (
          <div className="flex items-center gap-2">
            {canEdit ? (
              <button
                onClick={() =>
                  isEditing
                    ? handleSave(
                      row.original.facility_id,
                      row.original.source_id,
                      row.original.source_id,
                      row.original.source_type_id
                    )
                    : handleEdit(
                      row.original.facility_id,
                      row.original.source_id,
                      row.original.credits
                    )
                }
                className="ml-2 flex items-center gap-2"
                disabled={updateCreditsLoading} // Disable during loading
              >
                {isEditing ? (
                  <>
                    <i className="fa-solid fa-floppy-disk"></i> Save
                    {updateCreditsLoading && (
                      <i className="fa-solid fa-spinner fa-spin text-gray-500"></i>
                    )}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-pencil"></i> Edit
                  </>
                )}
              </button>
            ) : (
              <span>No Action Available</span>
            )}
          </div>
        );
      },
    },
  ];

  const totalRecords = membershipData?.filtered_counts ?? 0;
  useEffect(() => {
    if (isError && navigator.onLine) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: typedError.data?.detail ?? typedError.data?.message,
      });
    }
  }, [isError]);
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <>
      <h1 className="font-bold text-xl mb-2 ">Facilities</h1>
      <Separator />
      {/* Data Table */}
      <div className="mt-4 bg-white">
        <DataTable columns={columns} data={tableData} isLoading={isLoading} />{" "}
        {/* //isLoading */}
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}
    </>
  );
};

export default MemberMembershipFacilities;
interface EditableCreditInputProps {
  value: number;
  onChange: (value: number) => void;
  disable: boolean;
}
const EditableCreditInput: React.FC<EditableCreditInputProps> = memo(
  ({ value, onChange, disable }) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    const handleInputChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const inputValue = e.target.value;
      // Allow empty input and treat it as 0
      if (inputValue === "") {
        onChange(0);
        return;
      }
      // Allow only integers
      const parsedValue = parseInt(inputValue, 10);
      if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 1000) {
        onChange(parsedValue);
      }
    };
    return (
      <FloatingLabelInput
        type="number"
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        disabled={disable}
        className="border rounded px-2 py-1 w-20 h-6"
      />
    );
  }
);

EditableCreditInput.displayName = "EditableCreditInput";
// export default EditableCreditInput;
